/* eslint-disable react/no-unescaped-entities */
/**
 * /restaurant-agreement — the Restaurant Agreement template handed to
 * venue partners.  Includes the 4 cross-redemption clauses introduced
 * in the Cross-Redemption Network rollout:
 *
 *   1. Hustle Liability for Redeemed Platform Credits
 *   2. Customer Acquisition Moat (contact handover)
 *   3. Network Reciprocity (your customers spend at peer venues too)
 *   4. Reimbursement Schedule & Reconciliation
 *
 * Rendered as a printable A4 doc with screen styles + @media print
 * overrides so a venue can save-as-PDF straight from the browser.
 */
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer, FileText, ShieldCheck, Banknote, Users, Repeat } from "lucide-react";
import "./RestaurantAgreementPage.css";

const TODAY = new Date().toLocaleDateString("en-AU", {
  day: "2-digit", month: "long", year: "numeric",
});

export default function RestaurantAgreementPage() {
  const [params] = useSearchParams();
  const venue   = params.get("venue")   || "[Venue Name]";
  const abn     = params.get("abn")     || "[Venue ABN]";
  const address = params.get("address") || "[Venue Trading Address]";

  const today = useMemo(() => TODAY, []);

  return (
    <div className="ra-root" data-testid="restaurant-agreement-root">
      {/* Print bar — hidden when printed */}
      <div className="ra-printbar">
        <div>
          <strong>HustleEats Restaurant Agreement</strong>
          <span className="ra-version"> · v3.0 · {today}</span>
        </div>
        <button onClick={() => window.print()} className="ra-print-btn"
                data-testid="ra-print">
          <Printer size={16} /> Print / Save as PDF
        </button>
      </div>

      <article className="ra-doc">
        <header className="ra-header">
          <div className="ra-logo">Hustle<span>Eats</span></div>
          <h1>Restaurant Partnership Agreement</h1>
          <div className="ra-parties">
            <div>
              <span className="ra-party-label">Between</span>
              <strong>Hustle Group Pty Ltd</strong>
              <span>ACN 678 901 234 · the Platform</span>
            </div>
            <div>
              <span className="ra-party-label">And</span>
              <strong>{venue}</strong>
              <span>ABN {abn} · {address}</span>
            </div>
          </div>
          <p className="ra-effective">
            Effective from the date the Venue accepts these terms via the
            HustleEats onboarding flow.
          </p>
        </header>

        {/* ── Existing clauses 1-6 (placeholder for the demo) ───── */}
        <section className="ra-section">
          <h2>1. Scope &amp; Services</h2>
          <p>
            Hustle will list the Venue in the HustleEats directory,
            provide a free booking page, deliver newsletter inclusion in
            the relevant suburban edition, and accept HustleApp
            customer payments on the Venue's behalf.
          </p>

          <h2>2. Fees</h2>
          <p>
            No setup fee, no monthly minimum, no per-cover commission for
            walk-in diners.  A 1.4% + 30c card-processing pass-through
            applies to in-app payments. Add-on modules (Floorplan
            Designer, Diamond Templates, FOH-OS) are priced separately
            inside the admin dashboard.
          </p>

          <h2>3. Menu &amp; Brand Control</h2>
          <p>
            The Venue retains full ownership of menu copy, pricing,
            imagery and brand. Hustle will not push promotions to the
            Venue's diners without prior written consent and will never
            white-label the Venue's brand under another name.
          </p>

          <h2>4. Data Ownership</h2>
          <p>
            Diner contact data captured through the HustleApp at the
            Venue belongs to the Venue (see Clause 8 below). Hustle
            retains usage rights to platform-wide anonymised analytics
            only.
          </p>

          <h2>5. Service Levels</h2>
          <p>
            Hustle commits to 99.5% monthly uptime on the booking,
            ordering and POS-credit APIs. Outage credits apply per
            Schedule&nbsp;A.
          </p>

          <h2>6. Term &amp; Termination</h2>
          <p>
            Either party may terminate with 30 days' written notice.
            Outstanding reimbursements owed to the Venue (Clause 10)
            will be settled within 14 days of termination.
          </p>
        </section>

        {/* ── 4 NEW Cross-Redemption clauses ───────────────────── */}
        <section className="ra-section ra-section--highlight"
                 data-testid="ra-cross-redemption-section">
          <div className="ra-section-badge">
            <ShieldCheck size={16} /> Cross-Redemption Network
          </div>
          <p className="ra-section-intro">
            Clauses 7–10 below describe how the Venue benefits from
            being part of the HustleApp cross-redemption network — the
            mechanism by which a customer's platform Hustle Dollars
            (earned anywhere on the Hustle Group) can be spent at the
            Venue, and vice versa.
          </p>

          <h2>
            <Banknote size={20} /> 7. Hustle's Liability for Platform Credits
          </h2>
          <p>
            When a customer redeems Hustle Dollars (HD$) at the Venue
            against a bill, the Venue is to treat HD$ as cash and
            present the same change / receipt experience to the
            customer.  Hustle accepts the credit liability — the
            full HD$ amount becomes an accounts-payable obligation
            from Hustle to the Venue, recorded automatically in the
            Venue's HustleEats admin under "Reimbursements".
            <strong> The Venue is never out of pocket for accepting
            HD$.</strong>
          </p>

          <h2>
            <Users size={20} /> 8. Customer Acquisition Moat
          </h2>
          <p>
            Every time a HustleApp customer redeems credits at the
            Venue — and every time the HustleApp records a visit
            without redemption — the customer's contact details
            (name, email, mobile, dietary tags, last-visit date) are
            handed to the Venue and merged into the Venue's own CRM
            in the HustleEats admin.  These records are licensed
            <strong> in perpetuity </strong> to the Venue for direct
            marketing purposes, regardless of whether the customer
            continues to use HustleApp.  No other booking, voucher or
            delivery platform in Australia delivers customer data to
            the venue with this depth of attribution.
          </p>

          <h2>
            <Repeat size={20} /> 9. Network Reciprocity
          </h2>
          <p>
            In return for accepting HD$ at the Venue, the Venue's own
            issued credits (e.g. refunds, loyalty top-ups, gift
            cards) earn customers spendable balance across the
            HustleApp network.  This pulls the Venue's diners back
            into the wider Hustle ecosystem — they discover, book and
            buy at peer venues using the credit you funded — which
            keeps your brand top-of-mind across every other touch-
            point in the customer's week.  Net-net the Venue gains
            distribution leverage that is impossible to replicate
            with stand-alone loyalty schemes.
          </p>

          <h2>
            <FileText size={20} /> 10. Reimbursement Schedule &amp; Reconciliation
          </h2>
          <p>
            HD$ redemptions are paid out by Hustle in arrears on a
            <strong> 14-day rolling cycle</strong>. Each payout is
            referenced against the line-items visible in the Venue's
            "Reimbursements" tab.  Payments are made by EFT to the
            Venue's nominated bank account or, at the Venue's
            election, settled by net-off against any subscription
            fees due to Hustle in the same period.  Disputed line-
            items must be raised within 30 days of the payout
            reference date; un-disputed line-items are deemed
            accepted and final after that window.
          </p>
        </section>

        {/* ── Signature block ───────────────────────────────────── */}
        <section className="ra-section ra-signatures">
          <h2>11. Acceptance</h2>
          <p>
            By clicking <em>"I accept"</em> in the HustleEats
            onboarding flow, the Venue's authorised signatory
            confirms they have read, understood and agree to the
            entirety of this agreement including the Cross-
            Redemption clauses 7 — 10.
          </p>

          <div className="ra-sigs">
            <div>
              <span className="ra-siglabel">For the Venue</span>
              <div className="ra-sigline" />
              <span className="ra-signote">Authorised Signatory · Date</span>
            </div>
            <div>
              <span className="ra-siglabel">For Hustle Group Pty Ltd</span>
              <div className="ra-sigline ra-sigline--filled">
                D. Wagner — Director
              </div>
              <span className="ra-signote">{today}</span>
            </div>
          </div>
        </section>

        <footer className="ra-footer">
          HustleEats Restaurant Agreement · v3.0 · Cross-Redemption
          Network update · Hustle Group Pty Ltd
        </footer>
      </article>
    </div>
  );
}
